
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getShoppingLists, createShoppingList, deleteShoppingList } from '@/services/shopping/lists.service';
import { networkService } from '@/services/network.service';
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { ShoppingList } from '@/types';
import { syncService } from '@/services/sync.service';
import { safeToastError, safeToastInfo, safeToastSuccess } from '@/components/ui/toastManager';

// Helper function to convert SyncableShoppingList to ShoppingList
const convertToShoppingList = (syncableList: any): ShoppingList => ({
  id: syncableList.id,
  name: syncableList.name,
  user_id: syncableList.user_id,
  isPrimary: syncableList.is_primary || false,
  is_primary: syncableList.is_primary || false,
  rapid_insert: syncableList.rapid_insert || false,
  shop_id: syncableList.shop_id,
  backgroundGradient: syncableList.background_gradient,
  background_gradient: syncableList.background_gradient,
  createdAt: typeof syncableList.created_at === 'string' 
    ? new Date(syncableList.created_at) 
    : syncableList.created_at || new Date(),
  updatedAt: typeof syncableList.updated_at === 'string' 
    ? new Date(syncableList.updated_at) 
    : syncableList.updated_at || new Date(),
  items: [],
  collaborators: []
});

export function useOfflineShoppingLists() {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const queryClient = useQueryClient();
  
  // Update online status when network changes
  useEffect(() => {
    const handleNetworkChange = (status: { connected: boolean }) => {
      setIsOnline(status.connected);
    };
    
    const unsubscribe = networkService.addListener(handleNetworkChange);
    return () => unsubscribe();
  }, []);
  
  // ✅ FIX: Migliorata gestione visualizzazione liste offline con fallback robusto
  const { data: shoppingLists, isLoading, error, refetch } = useQuery({
    queryKey: ['shopping-lists'],
    queryFn: async () => {
      if (networkService.isOnline()) {
        try {
          //OfflineLogger.info('useOfflineShoppingLists', 'Fetching lists online');
          const onlineLists = await getShoppingLists();
          
          // Cache offline delle liste online
          await Promise.all(onlineLists.map(async list => {
            try {
              await offlineDb.shoppingLists.put({
                id: list.id,
                name: list.name,
                user_id: list.user_id,
                is_primary: list.isPrimary || list.is_primary,
                rapid_insert: list.rapid_insert,
                shop_id: list.shop_id,
                background_gradient: list.backgroundGradient || list.background_gradient,
                created_at: list.createdAt?.toISOString() || new Date().toISOString(),
                updated_at: list.updatedAt?.toISOString() || new Date().toISOString(),
                _syncStatus: 'synced' as const,
                _lastModified: Date.now()
              });
            } catch (err) {
              OfflineLogger.error('useOfflineShoppingLists.cacheList', `Error caching list ${list.id}: ${err}`);
            }
          }));
          
          //OfflineLogger.info('useOfflineShoppingLists', `Cached ${onlineLists.length} shopping lists offline`);
          return onlineLists;
        } catch (error) {
          OfflineLogger.error('useOfflineShoppingLists.fetchOnline', error);
          // Se il fetch online fallisce, usa i dati offline
          const offlineLists = await offlineDb.shoppingLists
            .where('_syncStatus')
            .notEqual('deleted')
            .toArray();
          //OfflineLogger.info('useOfflineShoppingLists', `Using ${offlineLists.length} offline lists as fallback`);
          return offlineLists.map(convertToShoppingList);
        }
      } else {
        // ✅ FIX: Modalità offline migliorata con logging dettagliato
        //OfflineLogger.info('useOfflineShoppingLists', 'Using offline mode');
        const offlineLists = await offlineDb.shoppingLists
          .where('_syncStatus')
          .notEqual('deleted')
          .toArray();
        
        //OfflineLogger.info('useOfflineShoppingLists', `Found ${offlineLists.length} lists in offline storage`);
        
        // ✅ FIX: Se non ci sono liste offline, crea automaticamente la lista primaria
        if (offlineLists.length === 0) {
          //OfflineLogger.info('useOfflineShoppingLists', 'No offline lists found, creating primary list');
          const userId = localStorage.getItem('userId') || 'offline-user';
          const primaryList = {
            id: crypto.randomUUID(),
            name: 'Lista della spesa',
            is_primary: true,
            user_id: userId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            rapid_insert: false,
            shop_id: null,
            background_gradient: null,
            _syncStatus: 'pending' as const,
            _lastModified: Date.now()
          };
          
          await offlineDb.shoppingLists.add(primaryList);
          await syncService.addToSyncQueue('shopping_lists', 'create', primaryList.id, primaryList);
          
          return [convertToShoppingList(primaryList)];
        }
        
        return offlineLists.map(convertToShoppingList);
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const createList = async (data: { name: string; isPrimary?: boolean }) => {
    if (isOnline) {
      try {
        await createShoppingList(data.name);
        await refetch();
        safeToastSuccess('Lista creata con successo');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.createList', error);
        safeToastError('Errore durante la creazione della lista');
      }
    } else {
      // ✅ FIX: Migliora la creazione offline
      try {
        const userId = localStorage.getItem('userId') || 'offline-user';
        const newList = {
          id: crypto.randomUUID(),
          name: data.name,
          is_primary: data.isPrimary || false,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          rapid_insert: false,
          shop_id: null,
          background_gradient: null,
          _syncStatus: 'pending' as const,
          _lastModified: Date.now()
        };
        
        await offlineDb.shoppingLists.add(newList);
        await syncService.addToSyncQueue('shopping_lists', 'create', newList.id, newList);
        await refetch();
        safeToastInfo('Lista creata offline - verrà sincronizzata al ritorno online');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.createListOffline', error);
        safeToastError('Errore durante la creazione della lista offline');
      }
    }
  };

  const updateList = async (listId: string, updates: Partial<ShoppingList>) => {
    if (isOnline) {
      try {
        // ✅ FIX: Per ora solo aggiornamenti offline supportati
        //OfflineLogger.info('useOfflineShoppingLists.updateList', 'Update functionality not yet implemented for online mode');
        safeToastInfo('Aggiornamento liste supportato solo offline al momento');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.updateList', error);
        safeToastError('Errore durante l\'aggiornamento della lista');
      }
    } else {
      // ✅ FIX: Migliora l'aggiornamento offline
      try {
        const updateData = {
          name: updates.name,
          is_primary: updates.isPrimary || updates.is_primary,
          rapid_insert: updates.rapid_insert,
          shop_id: updates.shop_id,
          background_gradient: updates.backgroundGradient || updates.background_gradient,
          updated_at: new Date().toISOString(),
          _syncStatus: 'pending' as const,
          _lastModified: Date.now()
        };
        
        await offlineDb.shoppingLists.update(listId, updateData);
        await syncService.addToSyncQueue('shopping_lists', 'update', listId, updateData);
        await refetch();
        safeToastInfo('Lista aggiornata offline - verrà sincronizzata al ritorno online');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.updateListOffline', error);
        safeToastError('Errore durante l\'aggiornamento della lista offline');
      }
    }
  };

  const deleteList = async (listId: string) => {
    if (isOnline) {
      try {
        await deleteShoppingList(listId);
        await refetch();
        safeToastSuccess('Lista eliminata con successo');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.deleteList', error);
        safeToastError('Errore durante l\'eliminazione della lista');
      }
    } else {
      // ✅ FIX: Migliora l'eliminazione offline
      try {
        await offlineDb.shoppingLists.update(listId, {
          _syncStatus: 'deleted' as const,
          _lastModified: Date.now()
        });
        await syncService.addToSyncQueue('shopping_lists', 'delete', listId);
        await refetch();
        safeToastInfo('Lista eliminata offline - verrà sincronizzata al ritorno online');
      } catch (error) {
        OfflineLogger.error('useOfflineShoppingLists.deleteListOffline', error);
        safeToastError('Errore durante l\'eliminazione della lista offline');
      }
    }
  };

  const refreshLists = () => {
    refetch();
  };
  
  return {
    shoppingLists: shoppingLists || [],
    isLoading,
    error,
    isOnline,
    refreshLists: refetch,
    createList,
    updateList,
    deleteList
  };
}
